import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, User, Store, ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { TwoFactorVerification } from '@/components/auth/TwoFactorVerification';

const emailSchema = z.string().trim().email({ message: "Please enter a valid email" });

interface AuthSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

type AuthMode = 'signin' | 'signup' | 'forgot';

interface PendingUser {
  id: string;
  email: string;
}

export const AuthSheet: React.FC<AuthSheetProps> = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState<'user' | 'owner'>('user');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingUser, setPendingUser] = useState<PendingUser | null>(null);
  const { toast } = useToast();

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth('google', {
        redirect_uri: window.location.origin,
      });
      
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
      setGoogleLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setAppleLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth('apple', {
        redirect_uri: window.location.origin,
      });
      
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
      setAppleLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`
          }
        });
        
        if (error) throw error;
        
        // Assign the selected role after successful signup
        if (data.user) {
          const { error: roleError } = await supabase.rpc('assign_user_role', {
            selected_role: selectedRole
          });
          
          if (roleError) {
            console.error('Error assigning role:', roleError);
          }
          
          // Send welcome email
          supabase.functions.invoke('send-welcome-email', {
            body: {
              email,
              role: selectedRole
            }
          }).catch(err => console.error('Error sending welcome email:', err));
        }
        
        toast({
          title: 'Account created!',
          description: `You're now registered as ${selectedRole === 'owner' ? 'an Owner' : 'a User'}.`
        });
        setMode('signin');
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        
        if (error) throw error;
        
        // Check if user has 2FA enabled using security definer function
        if (data.user) {
          const { data: totpData, error: totpError } = await supabase
            .rpc('get_totp_secret_for_verification', { p_user_id: data.user.id });

          if (!totpError && totpData && totpData.length > 0 && totpData[0].is_enabled) {
            // User has 2FA enabled, show verification screen
            setPendingUser({ id: data.user.id, email: data.user.email || email });
            setRequires2FA(true);
            // Sign out temporarily until 2FA is verified
            await supabase.auth.signOut();
            setLoading(false);
            return;
          }
        }
        
        toast({
          title: 'Welcome back!',
          description: 'You have successfully signed in.'
        });
        onClose();
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      toast({
        title: 'Invalid email',
        description: result.error.errors[0].message,
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/profile`
      });
      
      if (error) throw error;
      
      setResetSent(true);
      toast({
        title: 'Reset email sent',
        description: 'Check your inbox for the password reset link.'
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setResetSent(false);
  };

  const handle2FASuccess = async () => {
    // Re-sign in after successful 2FA
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast({
        title: 'Error',
        description: 'Session expired. Please sign in again.',
        variant: 'destructive',
      });
      setRequires2FA(false);
      setPendingUser(null);
      return;
    }

    toast({
      title: 'Welcome back!',
      description: 'You have signed in successfully',
    });
    onClose();
  };

  const handle2FACancel = () => {
    setRequires2FA(false);
    setPendingUser(null);
    setPassword('');
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[9998]"
        onClick={onClose}
      />
      
      {/* Sheet */}
      <div className={`fixed right-0 top-0 h-full w-full max-w-md bg-card border-l border-border z-[9999] shadow-2xl transition-transform duration-300 ${isOpen ? 'animate-slide-in-right' : ''}`}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-8 right-8 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X size={24} />
        </button>

        {/* Content */}
        <div className="flex flex-col h-full px-10 pt-24 pb-10 overflow-y-auto">
          {/* 2FA Verification */}
          {requires2FA && pendingUser ? (
            <TwoFactorVerification
              userId={pendingUser.id}
              userEmail={pendingUser.email}
              onSuccess={handle2FASuccess}
              onCancel={handle2FACancel}
            />
          ) : (
            <>
              {/* Back button for forgot password */}
              {mode === 'forgot' && (
                <button
                  onClick={() => switchMode('signin')}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6 text-sm"
                >
                  <ArrowLeft size={16} />
                  Back to sign in
                </button>
              )}

              <h2 className="text-foreground text-4xl font-semibold mb-2">
                {mode === 'signup' ? 'Create Account' : mode === 'forgot' ? 'Reset Password' : 'Sign In'}
              </h2>
              <p className="text-muted-foreground text-sm mb-8">
                {mode === 'signup' 
                  ? 'Join us to create and manage your events' 
                  : mode === 'forgot'
                  ? 'Enter your email and we\'ll send you a reset link'
                  : 'Welcome back! Please sign in to continue'}
              </p>

          {mode === 'forgot' ? (
            // Forgot password form
            resetSent ? (
              <div className="bg-muted/50 border border-border rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-foreground font-semibold mb-2">Check your email</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  We've sent a password reset link to <strong className="text-foreground">{email}</strong>
                </p>
                <button
                  onClick={() => switchMode('signin')}
                  className="text-brand hover:text-brand/80 text-sm font-medium transition-colors"
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="flex flex-col gap-6">
                <div>
                  <label htmlFor="reset-email" className="block text-foreground text-sm font-medium mb-2">
                    Email address
                  </label>
                  <input
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-background border border-input text-foreground rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring transition-all placeholder:text-muted-foreground"
                    placeholder="your@email.com"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand text-brand-foreground font-semibold py-3 px-6 rounded-lg hover:bg-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>
            )
          ) : (
            // Sign in / Sign up form
            <>
              {/* Social Login Buttons */}
              <div className="space-y-3">
                {/* Google Login Button */}
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={googleLoading}
                  className="w-full flex items-center justify-center gap-3 bg-background border border-border text-foreground font-medium py-3 px-6 rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {googleLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  )}
                  Continue with Google
                </button>
              </div>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>

              <form onSubmit={handleAuth} className="flex flex-col gap-6">
                <div>
                  <label htmlFor="email" className="block text-foreground text-sm font-medium mb-2">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-background border border-input text-foreground rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring transition-all placeholder:text-muted-foreground"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-foreground text-sm font-medium mb-2">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full bg-background border border-input text-foreground rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring transition-all placeholder:text-muted-foreground"
                    placeholder="••••••••"
                  />
                  {mode === 'signin' && (
                    <button
                      type="button"
                      onClick={() => switchMode('forgot')}
                      className="text-brand hover:text-brand/80 text-sm mt-2 transition-colors"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>

                {/* Role Selection - Only show on signup */}
                {mode === 'signup' && (
                  <div>
                    <label className="block text-foreground text-sm font-medium mb-3">
                      I want to
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setSelectedRole('user')}
                        className={`flex flex-col items-center gap-2 p-4 border rounded-lg transition-all ${
                          selectedRole === 'user'
                            ? 'border-brand bg-brand/10 text-foreground'
                            : 'border-border text-muted-foreground hover:border-foreground/30'
                        }`}
                      >
                        <User className="w-6 h-6" />
                        <span className="text-sm font-medium">Discover</span>
                        <span className="text-xs opacity-70">Find events & experiences</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedRole('owner')}
                        className={`flex flex-col items-center gap-2 p-4 border rounded-lg transition-all ${
                          selectedRole === 'owner'
                            ? 'border-brand bg-brand/10 text-foreground'
                            : 'border-border text-muted-foreground hover:border-foreground/30'
                        }`}
                      >
                        <Store className="w-6 h-6" />
                        <span className="text-sm font-medium">List & Manage</span>
                        <span className="text-xs opacity-70">Add my business</span>
                      </button>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand text-brand-foreground font-semibold py-3 px-6 rounded-lg hover:bg-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Please wait...
                    </>
                  ) : mode === 'signup' ? (
                    'Create Account'
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>
            </>
          )}

              {mode !== 'forgot' && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => switchMode(mode === 'signup' ? 'signin' : 'signup')}
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    {mode === 'signup' 
                      ? 'Already have an account? Sign in' 
                      : "Don't have an account? Create one"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};
